/**
 * @file 取り込んだ資料のスキャン (1 問 = 1 画像) の閲覧・読み取り確認のクライアント。
 * @what: 一覧 (ページ順) の取得と、読み取り確認 (合っている / 誤読あり + 修正メモ) の更新。
 *   画像本体は Firebase Storage から直接取得する (storage.rules の family member read)。
 * @why: 資料 (StudySource) は子に従属しない家族の持ち物なので、学習者を経路に含めない
 *   (代表 2026-08-22)。
 * @failure: いずれも ApiError を伝播。一覧が空 = まだ取り込みが無い、は正常系。
 */

import { apiFetch } from "@/shared/api/client";

export type StudyScanReviewStatus = "unchecked" | "ok" | "wrong";

export interface StudyScanItem {
  id: string;
  image_path: string;
  content_type: string;
  index: Record<string, string>;
  review_status: StudyScanReviewStatus;
  review_note?: string;
  reviewed_at?: string;
  updated_at: string;
}

function scanItemsPath(familyId: string, sourceId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/sources/${encodeURIComponent(
    sourceId,
  )}/scan_items`;
}

export async function fetchScanItems(
  familyId: string,
  sourceId: string,
  signal?: AbortSignal,
): Promise<StudyScanItem[]> {
  const res = await apiFetch<{ items: StudyScanItem[] }>(
    scanItemsPath(familyId, sourceId),
    signal ? { signal } : {},
  );
  return res.items;
}

export async function putScanReview(
  familyId: string,
  sourceId: string,
  itemId: string,
  body: { status: StudyScanReviewStatus; note?: string },
): Promise<StudyScanItem> {
  return apiFetch<StudyScanItem>(
    `${scanItemsPath(familyId, sourceId)}/${encodeURIComponent(itemId)}/review`,
    { method: "PUT", body },
  );
}

/** 索引フィールドの表示順。ここに無いキーは末尾に名前順で続く。 */
export const SCAN_INDEX_FIELD_ORDER: readonly string[] = [
  "分野",
  "単元",
  "考査",
  "図の説明",
  "指示",
  "正答",
  "難易度",
  "出典校",
  "学校難度",
  "出典",
  "タグ",
];

export function orderedIndexEntries(index: Record<string, string>): [string, string][] {
  const known = SCAN_INDEX_FIELD_ORDER.filter((k) => index[k] !== undefined).map(
    (k) => [k, index[k]!] as [string, string],
  );
  const rest = Object.keys(index)
    .filter((k) => !SCAN_INDEX_FIELD_ORDER.includes(k))
    .sort()
    .map((k) => [k, index[k]!] as [string, string]);
  return [...known, ...rest];
}
